import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input, Select, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import useSearchParams from '@hooks/useSearchParams';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { format } from 'date-fns/esm';
import { enUS, ptBR } from 'date-fns/locale';
import { useAlarmMenuFilter } from '@hooks/useAlarmMenuFilter';
import rolesConstants from '@utils/rolesConstants';
import * as Styled from '../Styles';

const { Search } = Input;

export interface IAlertHistory {
  dataCriacao: string;
  dataUltimaAlteracao: string;
  customerId: number;
  customerName: string;
  level: string | null;
  nomeAlarme: string;
  valorParaDisparo: number;
  status: string;
  quantidade: number | null;
  qtdMin: number | null;
  qtdMax: number | null;
  idAlarme: string;
  nomeGrafico: string;
  dataHistoricoAlarme: string | null;
  tipoAlarme: string;
  tipoAlarmeOriginal: string | null;
}

interface PageDashBoardResult<T> {
  content: T[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  totalElements: number;
}

const AlarmHistory: React.FC = () => {
  const { t, i18n } = useTranslation([
    'alerts',
    'attributes',
    'create-alarm-step',
  ]);
  const currentLanguage = i18n.language;

  const [nomeAlarmeValue, setNomeAlarmeValue] = useState('');
  const [tipoAlarmeValue, setTipoAlarmeValue] = useState<string | undefined>();
  const [levelValue, setLevelValue] = useState<string | undefined>();

  const {
    customer: { customerLogged },
    theme,
  } = useContext(SessionContext);

  const {
    allMenuFilter,
    defaultFilterValue,
    hasSimcardsPermission,
    hasTransactionsPermission,
  } = useAlarmMenuFilter({
    simcardsPermission: rolesConstants.INSIGHTS_ALARM_HISTORY,
    transactionsPermission: rolesConstants.INSIGHTS_TRANSACTIONS_VIEW,
  });

  const customerId = customerLogged?.id;

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'nomeAlarme',
      'tipoAlarme',
      'customerIdString',
      'status',
      'level',
      'nomeGrafico',
    ],
  });

  const {
    nomeAlarme,
    tipoAlarme,
    status,
    level,
    nomeGrafico,
    page = 1,
    linesPerPage = 20,
  } = useMemo(() => searchParams, [searchParams]);

  const [activeMenuFilter, setActiveMenuFilter] = useState<string>(
    nomeGrafico || '',
  );

  useEffect(() => {
    setActiveMenuFilter(nomeGrafico || '');
  }, [nomeGrafico]);

  useEffect(() => {
    setNomeAlarmeValue(nomeAlarme || '');
  }, [nomeAlarme]);

  useEffect(() => {
    setLevelValue(level);
  }, [level]);

  useEffect(() => {
    setTipoAlarmeValue(tipoAlarme);
  }, [tipoAlarme]);

  const { data: alertHistoryResponse, isLoading: isLoadingHistory } = useSwr<
    PageDashBoardResult<IAlertHistory>
  >(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar/historico'
      : null,
    {
      params: {
        ...(activeMenuFilter ? { nomeGrafico: activeMenuFilter } : {}),
        ...(nomeAlarme ? { nomeAlarme } : {}),
        ...(status ? { status } : {}),
        ...(level ? { level } : {}),
        ...(tipoAlarme ? { tipoAlarme } : {}),
        page: Number(page),
        size: Number(linesPerPage),
      },
      headers: {
        customerId,
      },
    },
  );

  const alertHistory = useMemo(() => {
    return alertHistoryResponse?.content || [];
  }, [alertHistoryResponse]);

  const alarmTypeOptions = useMemo(
    () => [
      {
        label: t('alerts:table-filter.by-alarm-type-threshold'),
        value: 'threshold',
      },
      {
        label: t(
          'alerts:table-filter.by-alarm-type-variation-percentual-history',
        ),
        value: 'variacao_percentual',
      },
      {
        label: t('alerts:table-filter.by-alarm-type-seasonal'),
        value: 'sazonal',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (
      activeMenuFilter === 'simcard_produto' &&
      !hasSimcardsPermission &&
      hasTransactionsPermission
    ) {
      setActiveMenuFilter(defaultFilterValue);
      setSearchParams({ page: 1 });
    }
  }, [
    activeMenuFilter,
    hasSimcardsPermission,
    hasTransactionsPermission,
    defaultFilterValue,
    setSearchParams,
  ]);

  const dateFormatConfig = useMemo(() => {
    if (currentLanguage === 'en') {
      return {
        locale: enUS,
        dateFormat: 'MM/dd/yyyy HH:mm:ss a',
      };
    }
    return {
      locale: ptBR,
      dateFormat: 'dd/MM/yyyy HH:mm:ss',
    };
  }, [currentLanguage]);

  const formatDate = useCallback(
    (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, dateFormatConfig.dateFormat, {
        locale: dateFormatConfig.locale,
      });
    },
    [dateFormatConfig],
  );

  const columns = useMemo(
    () => [
      {
        title: t('alerts:table-attributes.customer-name-field'),
        dataIndex: 'customerName',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.alert-name-field'),
        dataIndex: 'nomeAlarme',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.alarm-type-field'),
        dataIndex: 'tipoAlarme',
        align: 'center' as const,
        render: (alarmType: string, record: IAlertHistory) => {
          const displayType = record.tipoAlarmeOriginal || alarmType;
          if (!displayType || displayType === 'no_data') return '';
          return t(`alerts:alarm-types.${displayType}`);
        },
      },
      {
        title: t('alerts:table-attributes.graph-name-field'),
        dataIndex: 'nomeGrafico',
        align: 'center' as const,
        render: (graphName: string) => {
          if (!graphName) return '';
          return t(`alerts:graph-names.${graphName}`);
        },
      },
      {
        title: t('alerts:table-attributes.trigger-metric-field'),
        dataIndex: 'triggerMetric',
        align: 'center' as const,
        render: (_: undefined, record: IAlertHistory) => {
          if (record.qtdMin !== null && record.qtdMin !== undefined) {
            return t('alerts:table-attributes.trigger-metric-qtcMin-field');
          }
          if (record.qtdMax !== null && record.qtdMax !== undefined) {
            return t('alerts:table-attributes.trigger-metric-qtcMax-field');
          }
          return '';
        },
      },
      {
        title: t('alerts:table-attributes.value-to-trigger-history-field'),
        dataIndex: 'valorParaDisparo',
        align: 'center' as const,
        render: (value: number) => {
          if (!value && value !== 0) return '';
          return value.toString();
        },
      },
      {
        title: t('alerts:table-attributes.quantity-history-field'),
        dataIndex: 'quantidade',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.status-field'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (statusColumn: string) => {
          if (!statusColumn) return '';
          return statusColumn.charAt(0).toUpperCase() + statusColumn.slice(1);
        },
      },
      {
        title: t('alerts:table-attributes.level-field'),
        dataIndex: 'level',
        align: 'center' as const,
        render: (alarmLevel: string) => {
          if (alarmLevel === 'CRITICAL' || alarmLevel === 'CRIT') {
            return (
              <span
                style={{
                  color: '#D2211D',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  border: '1px solid #D2211D',
                }}
              >
                {alarmLevel === 'CRITICAL'
                  ? t('alerts:table-filter.by-level-critical')
                  : t('alerts:table-filter.by-level-crit')}
              </span>
            );
          }
          if (alarmLevel === 'OK' || alarmLevel === 'RECOVERY') {
            return (
              <span
                style={{
                  color: '#00BE14',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  border: '1px solid #00BE14',
                }}
              >
                {alarmLevel === 'OK'
                  ? t('alerts:table-filter.by-level-ok')
                  : t('alerts:table-filter.by-level-recovery')}
              </span>
            );
          }
          return (
            <span
              style={{
                color: theme.colors.text,
              }}
            >
              {alarmLevel}
            </span>
          );
        },
      },
      {
        title: t('alerts:table-attributes.alarm-history-date-field'),
        dataIndex: 'dataHistoricoAlarme',
        align: 'center' as const,
        render: (date: string) => formatDate(date),
      },
    ],
    [t, theme.colors.text, formatDate],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.graph-name-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={allMenuFilter}
            style={{ width: '100%' }}
            showSearch
            value={activeMenuFilter}
            onChange={value => {
              setActiveMenuFilter(value);
              setSearchParams({ nomeGrafico: value, page: 1 });
            }}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alert-name-label'),
        element: (
          <Search
            onSearch={value => {
              setSearchParams({ nomeAlarme: value, page: 1 });
            }}
            onChange={({ target: { value } }) => setNomeAlarmeValue(value)}
            value={nomeAlarmeValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alarm-type-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={alarmTypeOptions}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={tipoAlarmeValue}
            onChange={value => {
              setTipoAlarmeValue(value);
              setSearchParams({
                tipoAlarme: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.status-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('alerts:table-filter.by-status-active'),
                value: 'ativo',
              },
              {
                label: t('alerts:table-filter.by-status-inactive'),
                value: 'inativo',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={status}
            onChange={value => {
              setSearchParams({
                status: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alarm-last-change-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('alerts:table-filter.by-level-create') || 'CREATE',
                value: 'CREATE',
              },
              {
                label:
                  t('alerts:table-filter.by-level-update-status') ||
                  'UPDATE_STATUS',
                value: 'UPDATE_STATUS',
              },
              {
                label: t('alerts:table-filter.by-level-delete') || 'DELETE',
                value: 'DELETE',
              },
              {
                label: t('alerts:table-filter.by-level-critical') || 'CRITICO',
                value: 'CRITICAL',
              },
              {
                label: t('alerts:table-filter.by-level-ok') || 'OK',
                value: 'OK',
              },
              {
                label: t('alerts:table-filter.by-level-crit') || 'CRIT',
                value: 'CRIT',
              },
              {
                label: t('alerts:table-filter.by-level-recovery') || 'RECOVERY',
                value: 'RECOVERY',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={levelValue}
            onChange={value => {
              setLevelValue(value);
              setSearchParams({
                level: value,
                page: 1,
              });
            }}
          />
        ),
      },
    ],
    [
      t,
      allMenuFilter,
      activeMenuFilter,
      nomeAlarmeValue,
      alarmTypeOptions,
      tipoAlarmeValue,
      status,
      levelValue,
      setSearchParams,
    ],
  );

  return (
    <Styled.Container>
      <Table
        columns={columns}
        data={alertHistory.map((item, index) => ({
          ...item,
          key: `history-row-${index}`,
          id: item.idAlarme || `history-fallback-${index}`,
        }))}
        loading={isLoadingHistory}
        title={t('alerts:titles.alarm-history')}
        tableKey="alarm-history"
        customRowKey="key"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
          openByDefault: true,
        }}
        pagination={{
          page: Number(page),
          linesPerPage: Number(linesPerPage),
          total: alertHistoryResponse?.totalElements || 0,
        }}
        onTableChange={setSearchParams}
        disabledPaginationWhenAll={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('alerts:table.notFoundMessage')}
            />
          ),
        }}
      />
    </Styled.Container>
  );
};

export default AlarmHistory;
